/* eslint-disable react/require-default-props */
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Close from '@mui/icons-material/Close';
import i18n from 'i18next';

import Search from '@mui/icons-material/Search';

import { createIconComponent } from 'uikit/icons';
import { Button } from 'uikit/buttons';

import styles from '../styles/uikitInputsInput.module.scss';

const TextInput = forwardRef(
  (
    {
      ariaLabel = '',
      autoComplete = 'off',
      autoCorrect = 'off',
      buttonAriaLabel = i18n.t('clear-input', 'Clear input'),
      buttonTestId = '',
      className = '',
      icon = null,
      id,
      inputTestId = '',
      isDisabled = false,
      isError = false,
      isReadOnly = false,
      isRequired = false,
      maxLength = null,
      minLength = 0,
      name = '',
      onBlur = () => {},
      onChange = null,
      onClick = null,
      onFocus = () => {},
      onKeyDown = null,
      onKeyUp = () => {},
      placeholder = '',
      searchLabel = i18n.t('search', 'Search'),
      shouldHideBorder = false,
      shouldShowCloseButton = false,
      shouldUseSearchLabel = true,
      size = 'medium',
      spellCheck = 'false',
      testId = '',
      type = 'text',
      value = '',
    },
    ref,
  ) => {
    const CLASSES = {
      size: {
        small: styles.isSizeSmall,
        medium: styles.isSizeMedium, // default - no matching css class
        large: styles.isSizeLarge,
      },
    };

    const shouldBeSearchWithLabel = type === 'search' && shouldUseSearchLabel;

    const onCloseButtonClick = () => {
      if (onClick) onClick();
    };

    const getSearchLabelOrIcon = () => {
      if (!shouldBeSearchWithLabel && !icon) return null;
      if (shouldBeSearchWithLabel) {
        return (
          <label className={styles.iconLabel} htmlFor={id}>
            {createIconComponent({ icon: <Search size={16} /> })}
            <span>{searchLabel}</span>
          </label>
        );
      }
      return createIconComponent({
        icon,
        className: classnames(styles.icon, icon.props?.className),
      });
    };

    return (
      <div
        className={classnames(
          styles.element,
          {
            [styles.isIconVisible]: !!icon || shouldBeSearchWithLabel,
            [CLASSES.size[size]]: size,
            [styles.shouldHideBorder]: shouldHideBorder,
            [styles.isError]: isError,
            [styles.shouldShowCloseButton]: shouldShowCloseButton,
          },
          className,
        )}
        data-testid={testId}
      >
        {getSearchLabelOrIcon()}
        <input
          id={id}
          className={className}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          ref={ref}
          value={value}
          disabled={isDisabled}
          readOnly={isReadOnly}
          required={isRequired && !isReadOnly && !isDisabled}
          data-testid={inputTestId}
          autoComplete={autoComplete}
          autoCorrect={autoCorrect}
          spellCheck={spellCheck}
          name={name}
          aria-label={ariaLabel}
          maxLength={maxLength}
          minLength={minLength}
          type={type}
        />
        {shouldShowCloseButton && (
          <Button
            isDisabled={isDisabled}
            variant="ghost"
            icon={<Close />}
            size="xsmall"
            onClick={onCloseButtonClick}
            className={styles.button}
            data-testid={buttonTestId}
            ariaLabel={buttonAriaLabel}
          />
        )}
      </div>
    );
  },
);

TextInput.propTypes = {
  ariaLabel: PropTypes.string,
  autoComplete: PropTypes.oneOfType([PropTypes.oneOf(['on', 'off']), PropTypes.string]),
  autoCorrect: PropTypes.oneOf(['on', 'off']),
  buttonAriaLabel: PropTypes.string,
  buttonTestId: PropTypes.string,
  className: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  id: PropTypes.string.isRequired,
  inputTestId: PropTypes.string,
  isDisabled: PropTypes.bool,
  isError: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isRequired: PropTypes.bool,
  maxLength: PropTypes.number,
  minLength: PropTypes.number,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  placeholder: PropTypes.string,
  searchLabel: PropTypes.string,
  shouldHideBorder: PropTypes.bool,
  shouldShowCloseButton: PropTypes.bool,
  shouldUseSearchLabel: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  spellCheck: PropTypes.oneOf(['true', 'false']),
  testId: PropTypes.string,
  type: PropTypes.oneOf(['email', 'password', 'search', 'tel', 'text', 'time']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export { TextInput as TextInputStorybook };
export default React.memo(TextInput);
